import {ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {DataService} from '../../../../Services/data.service';
import {StorageService} from '../../../../Services/storage.service';
import {Title} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {ReportPrintService} from '../../../../Services/report-print.service';
import {SendEmailModalComponent} from '../../../common/send-email-modal/send-email-modal.component';
import {EmailModel} from '../../../../DataModels/email-model';
import {Modal} from 'bootstrap';
import {DateFilterService} from '../../../../Services/dateFilter.service';

export interface CrmFollowUpRow {
  interaction_id: string;
  user_id: string;
  salesperson_id: string;
  entity_name: string;
  contact_name: string;
  interaction_date: string;
  interaction_type: string;
  subject: string;
  outcome: string;
  opportunity_id: string | null;
  opportunity_title: string | null;
}

@Component({
  selector: 'app-crm-followup-report',
  templateUrl: './crm-followup-report.component.html',
  styleUrls: [],
  standalone: false,
})
export class CrmFollowUpReportComponent implements OnInit {

  @ViewChild('ReportOutput', {static: false}) ReportOutput: ElementRef;
  @ViewChild(SendEmailModalComponent, {read: ElementRef}) private localSendEmailModalHost: ElementRef;

  rptName = 'Follow-Up Required';
  isLoading = false;
  noDataFound = false;
  rows: CrmFollowUpRow[] = [];
  rowClasses: Map<string, string> = new Map();
  users: any[] = [];
  today = new Date();
  reportDataToSendInEmail: EmailModel = new EmailModel();

  buttonConfig: any = {
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: false,
    close: true,
    clear: false,
    filters: false,
    reset: true,
  };

  filters: any = {
    company_id: '',
    user_id: '',
    from_date: '',
    to_date: '',
  };

  vm: any = {Preferences: []};

  constructor(
    private titleService: Title,
    private router: Router,
    private tableDataService: DataService,
    private localStorage: StorageService,
    private notification: BsToastService,
    private reportPrint: ReportPrintService,
    private cd: ChangeDetectorRef,
    private dateFilter: DateFilterService,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle(this.rptName);
    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
    this.filters.company_id = this.vm.Preferences?.defaults?.company_id ?? '';

    this.tableDataService.getApiListData('sys_user', '?pageNumber=0&pageSize=999').subscribe({
      next: (result: any) => {
        this.users = result.Data ?? result.items ?? [];
      },
    });
  }

  getUserName(userId: string): string {
    if (!userId) {
      return '—';
    }
    const u = this.users.find(x => x.id === userId);
    return u ? u.name : userId;
  }

  rowClass(interactionDate: string): string {
    if (!interactionDate || interactionDate.startsWith('0001-') || interactionDate.endsWith('/0001')) {
      return 'text-danger';
    }
    const date = new Date(interactionDate);
    if (isNaN(date.getTime())) {
      return 'text-danger';
    }
    const diffDays = Math.floor((this.today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > 14) {
      return 'text-danger';
    }
    if (diffDays > 7) {
      return 'text-warning';
    }
    return '';
  }

  resetFilters() {
    this.filters = {
      company_id: this.vm.Preferences?.defaults?.company_id ?? '',
      user_id: '',
      from_date: '',
      to_date: '',
    };
    this.rows = [];
    this.rowClasses = new Map();
    this.vm.report = null;
  }

  close() {
    this.router.navigate(['/home']);
  }

  runReport() {
    this.isLoading = true;
    this.noDataFound = false;
    this.rows = [];

    let params = 'company_id=' + (this.filters.company_id || '');
    if (this.filters.user_id) {
      params += '&user_id=' + this.filters.user_id;
    }
    const fd = this.dateFilter.GetDateFormat(this.filters.from_date);
    const td = this.dateFilter.GetDateFormat(this.filters.to_date);
    if (fd) {
      params += '&from_date=' + fd;
    }
    if (td) {
      params += '&to_date=' + td;
    }

    this.tableDataService.getDataWithParams('CrmFollowUpReport', params).subscribe({
      next: (result: any) => {
        this.rows = Array.isArray(result) ? result : (result.Data ?? result.items ?? []);
        console.log('CrmFollowUpReport data:', this.rows);
        this.rowClasses = new Map(this.rows.map(r => [r.interaction_id, this.rowClass(r.interaction_date)]));
        this.noDataFound = this.rows.length === 0;
        this.vm.report = this.rows;
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.notification.showErrorToast('Error loading report.');
        this.isLoading = false;
      },
    });
  }

  printReport() {
    try {
      const html = this.buildPrintHtml();
      this.reportPrint.printInIframe(html);
    } catch (e) {
      console.error('CRM Follow-Up Report print failed', e);
    }
  }

  setEmailData() {
    const emailDocObj = {} as EmailModel;
    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '';
    emailDocObj.toEmail = '';
    emailDocObj.body = 'Please see the attached ' + this.rptName + '.<br><br>Best regards,<br><br>AcctXErp<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + this.rptName;
    try {
      this.cd.detectChanges();
    } catch {
    }
    try {
      emailDocObj.pdfHtml = this.buildPrintHtml(true);
    } catch {
      emailDocObj.pdfHtml = this.ReportOutput?.nativeElement?.outerHTML || '';
    }
    emailDocObj.activity_message = this.rptName;
    const now = new Date();
    emailDocObj.FileName = `CrmFollowUpReport_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.pdf`;
    this.reportDataToSendInEmail = emailDocObj;
    const element = document.getElementById('sendEmailModal') as HTMLElement;
    if (element) {
      const emailModal = new Modal(element);
      emailModal.show();
    }
  }

  private buildPrintHtml(forEmail: boolean = false): string {
    const rawContent = (this.ReportOutput?.nativeElement?.outerHTML || '').toString();
    const content = this.reportPrint.cleanContentHtml(rawContent);
    const meta: { label: string; value: string }[] = [];
    return this.reportPrint.buildHtml({title: this.rptName, contentHtml: content, meta, orientation: 'landscape'});
  }
}
